import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../components/page-container";
import Input from "../../components/input";
import Button from "../../components/button";
import Modal from "../../components/modal";
import { useClerkAuth } from "../../hooks/use-clerk-auth";
import { useTour } from "../../contexts/tour-context";
import apiClient from "../../services/api-client";
import signOutIcon from "../../assets/sign_out.svg";
import PageHeader from "../../components/page-header";
import s from "./styles.module.scss";
import { Loading } from "../loading";

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, logout, getSessionToken } = useClerkAuth();
  const { resetTour } = useTour();

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: user?.email || "",
    school: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    primaryButtonText: "OK",
    onPrimaryClick: null,
  });

  // Fetch existing settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.uid) return;

      try {
        setFetchingSettings(true);
        const result = await apiClient.get("/settings", {}, getSessionToken);

        if (result.status === "success" && result.data) {
          const settings = result.data;
          setFormData({
            firstName: settings.first_name || "",
            middleName: settings.middle_name || "",
            lastName: settings.last_name || "",
            email: settings.email || user?.email || "",
            school: settings.school || "",
          });
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        // Don't show error for fetching - just use defaults
      } finally {
        setFetchingSettings(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.email]);

  // Update email when user changes
  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user?.email, formData.email]);

  const validateForm = () => {
    const errors = {};

    // Required field validation
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    // Middle name is optional - no validation needed

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // School is optional - no validation needed

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const refetchSettings = async () => {
    if (!user?.uid) return;

    try {
      const result = await apiClient.get("/settings", {}, getSessionToken);

      if (result.status === "success" && result.data) {
        const settings = result.data;
        setFormData({
          firstName: settings.first_name || "",
          middleName: settings.middle_name || "",
          lastName: settings.last_name || "",
          email: settings.email || user?.email || "",
          school: settings.school || "",
        });
        // Clear any validation errors after successful refetch
        setValidationErrors({});
      }
    } catch (error) {
      console.error("Error refetching settings:", error);
    }
  };

  const handleSaveSettings = async () => {
    // Just return early if validation fails - let input errors show
    if (!validateForm()) {
      return;
    }

    if (!user?.uid) {
      setModalState({
        isOpen: true,
        type: "error",
        title: "Authentication Error",
        message: "Please sign in to save your settings.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        first_name: formData.firstName.trim(),
        middle_name: formData.middleName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        school: formData.school.trim(),
      };

      const result = await apiClient.post(
        "/settings",
        payload,
        getSessionToken
      );
      if (result.status !== "success") {
        throw new Error(result.message || "Operation failed");
      }

      // Show success modal with refetch functionality
      setModalState({
        isOpen: true,
        type: "success",
        title: "Settings saved successfully",
        message: "Your account settings have been updated.",
        primaryButtonText: "OK",
        onPrimaryClick: () => {
          closeModal();
          refetchSettings();
        },
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error saving settings",
        message: "Failed to save your settings. Please try again.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleReplayTour = async () => {
    await resetTour();
    navigate("/upload");
  };

  if (fetchingSettings) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <PageHeader title="Account Settings" />

      <div className={s.settingsForm}>
        <div className={s.formSection}>
          <label className={s.sectionLabel}>Name</label>
          <div className={s.nameFields}>
            <Input
              type="text"
              placeholder="First name *"
              value={formData.firstName}
              onChange={(value) => handleInputChange("firstName", value)}
              error={validationErrors.firstName}
            />
            <Input
              type="text"
              placeholder="Middle name (optional)"
              value={formData.middleName}
              onChange={(value) => handleInputChange("middleName", value)}
            />
            <Input
              type="text"
              placeholder="Last name *"
              value={formData.lastName}
              onChange={(value) => handleInputChange("lastName", value)}
              error={validationErrors.lastName}
            />
          </div>
        </div>

        <div className={s.formSection}>
          <label className={`${s.sectionLabel} ${s.required}`}>
            Enter your email address
          </label>
          <Input
            type="email"
            placeholder="user@mail.com *"
            value={formData.email}
            onChange={(value) => handleInputChange("email", value)}
            error={validationErrors.email}
          />
        </div>

        <div className={s.formSection}>
          <label className={s.sectionLabel}>School</label>
          <Input
            type="text"
            placeholder="Enter your school name here (optional)"
            value={formData.school}
            onChange={(value) => handleInputChange("school", value)}
          />
        </div>

        <div className={s.formSection}>
          <label className={s.sectionLabel}>Onboarding Tour</label>
          <p className={s.sectionDescription}>
            Replay the onboarding tour to review Ocean's main features and functionality.
          </p>
          <Button
            variant="secondary"
            onClick={handleReplayTour}
            className={s.replayTourButton}
            disabled={loading}
          >
            Replay Tour
          </Button>
        </div>
      </div>

      <div className={s.buttonContainer}>
        <Button
          variant="secondary"
          onClick={handleSaveSettings}
          className={s.saveButton}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>

        <Button
          variant="primary"
          onClick={handleSignOut}
          className={s.signOutButton}
          icon={<img src={signOutIcon} alt="Sign out" />}
          iconPosition="left"
          disabled={loading}
        >
          Sign out
        </Button>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        primaryButtonText={modalState.primaryButtonText}
        onPrimaryClick={modalState.onPrimaryClick}
      />
    </PageContainer>
  );
};

export default AccountSettings;
